import { createContext, useEffect, useState, type ReactNode } from "react";

type OrderType = "exchpart" | "sellpart";

export interface CartItem {
  goid: string;
  quantity: number;
  table: string;
  typeorder: OrderType;
}

interface CartContextProps {
  cart: CartItem[];
  addToCart: (item: Omit<CartItem, "quantity">, quantity?: number) => void;
  removeFromCart: (goid: string, typeorder: OrderType) => void;
  clearCart: () => void;
  increaseQuantity: (goid: string, typeorder: OrderType) => void;
  decreaseQuantity: (goid: string, typeorder: OrderType) => void;
}

const CART_KEY = "hixa_cart_v2";

export const CartContext = createContext<CartContextProps | undefined>(
  undefined
);

export const CartProvider = ({ children }: { children: ReactNode }) => {
  const [cart, setCart] = useState<CartItem[]>([]);

  // Завантаження з localStorage при ініціалізації
  useEffect(() => {
    const storedCart = localStorage.getItem(CART_KEY);
    if (storedCart) {
      setCart(JSON.parse(storedCart));
    }
  }, []);

  // Додай параметр quantity з урахуванням typeorder
  const addToCart = (
    item: Omit<CartItem, "quantity">,
    quantity: number = 1
  ) => {
    setCart((prevCart) => {
      const existing = prevCart.find(
        (i) => i.goid === item.goid && i.typeorder === item.typeorder
      );
      let newCart: CartItem[];
      if (existing) {
        newCart = prevCart.map((i) =>
          i.goid === item.goid && i.typeorder === item.typeorder
            ? { ...i, quantity: i.quantity + quantity }
            : i
        );
      } else {
        newCart = [...prevCart, { ...item, quantity }];
      }
      localStorage.setItem(CART_KEY, JSON.stringify(newCart));
      return newCart;
    });
  };

  // Збільшити кількість з урахуванням typeorder
  const increaseQuantity = (goid: string, typeorder: OrderType) => {
    setCart((prevCart) => {
      const newCart = prevCart.map((item) =>
        item.goid === goid && item.typeorder === typeorder
          ? { ...item, quantity: item.quantity + 1 }
          : item
      );
      localStorage.setItem(CART_KEY, JSON.stringify(newCart));
      return newCart;
    });
  };

  // Зменшити кількість (і видалити, якщо quantity = 1)
  const decreaseQuantity = (goid: string, typeorder: OrderType) => {
    setCart((prevCart) => {
      const newCart = prevCart
        .map((item) =>
          item.goid === goid && item.typeorder === typeorder
            ? { ...item, quantity: item.quantity - 1 }
            : item
        )
        .filter((item) => item.quantity > 0);
      localStorage.setItem(CART_KEY, JSON.stringify(newCart));
      return newCart;
    });
  };

  // Видалити товар з конкретним typeorder
  const removeFromCart = (goid: string, typeorder: OrderType) => {
    setCart((prevCart) => {
      const newCart = prevCart.filter(
        (item) => item.goid !== goid || item.typeorder !== typeorder
      );
      localStorage.setItem(CART_KEY, JSON.stringify(newCart));
      return newCart;
    });
  };

  const clearCart = () => {
    localStorage.setItem(CART_KEY, JSON.stringify([]));
    setCart([]);
  };

  return (
    <CartContext.Provider
      value={{
        cart,
        addToCart,
        removeFromCart,
        clearCart,
        increaseQuantity,
        decreaseQuantity,
      }}
    >
      {children}
    </CartContext.Provider>
  );
};
